// Post-generation action modal — shown after a PDF renders. Bold three-
// button row: Open / Print / Email. Bigger and more decisive than the
// rest of the chrome because the doctor just produced a clinical
// artifact and needs to act on it now.

import Modal, { SecondaryButton } from './Modal.jsx'

/**
 * @param {object} props
 * @param {object} props.doc          — the freshly-generated ClinicalDocument row
 * @param {string} props.kindLabel    — human label ("Sick Certificate")
 * @param {() => void} props.onClose
 * @param {(doc) => void} props.onEmail  — caller opens the EmailDocumentModal
 */
export default function PostGenerationModal({ doc, kindLabel, onClose, onEmail }) {
  if (!doc) return null

  const url = doc.file_url
  const handleOpen = () => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }
  const handlePrint = () => {
    if (!url) return
    // Best-effort auto-print: open a new tab and call print() once it
    // loads. Browser PDF viewers vary in their cooperation with this
    // pattern (Chrome's PDFium honours it; some Firefox builds don't),
    // so always also offer the manual Open path. The user can hit Ctrl+P
    // in the viewer if auto-print is suppressed.
    const win = window.open(url, '_blank')
    if (!win) return
    const tryPrint = () => { try { win.print() } catch { /* viewer will offer its own */ } }
    win.addEventListener('load', tryPrint)
    setTimeout(tryPrint, 1500)  // belt-and-braces for slow PDF viewers
  }

  return (
    <Modal
      title="Document ready"
      subtitle={`${kindLabel} generated and saved to the patient record.`}
      onClose={onClose}
    >
      <div className="text-sm text-slate-600 mb-4">
        The PDF is filed against this encounter and visible in the patient's record.
        What would you like to do with it now?
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <ActionButton
          icon="📄"
          label="Open"
          hint="View in a new tab"
          onClick={handleOpen}
        />
        <ActionButton
          icon="🖨️"
          label="Print"
          hint="Send to printer"
          onClick={handlePrint}
        />
        <ActionButton
          icon="✉️"
          label="Email"
          hint="Send to patient or other"
          onClick={() => onEmail?.(doc)}
        />
      </div>

      <div className="flex justify-end mt-5 pt-4 border-t-2 border-slate-100">
        <SecondaryButton onClick={onClose}>Done</SecondaryButton>
      </div>
    </Modal>
  )
}

function ActionButton({ icon, label, hint, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="bg-white border-2 border-slate-200 hover:border-wantok-600 hover:bg-wantok-50 rounded-xl px-4 py-5 text-center transition group"
    >
      <div className="text-3xl mb-1.5">{icon}</div>
      <div className="font-bold text-slate-800 group-hover:text-wantok-800">{label}</div>
      <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
    </button>
  )
}
